import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HotTable } from '@handsontable/react';
import { SpecraEmptyState, SpecraHeading, SpecraSpinner, SpecraSurface, SpecraTabBar, SpecraTabPanel, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { registerAllModules } from 'handsontable/registry';
import { useState } from 'react';
import 'handsontable/dist/handsontable.full.css';
import { loadDraftBytes, parseDraftOffice, previewFirstSheetOnly } from './parseDraftOffice';
registerAllModules();
export const DRAFT_PREVIEW_UNAVAILABLE = "Preview isn't available for this file. You can still create it and open it in the editor";
function sheetLimitLine(sheet) {
    const shown = Math.max(sheet.rows.length - sheet.frozenRows, 0);
    return `Showing ${shown.toLocaleString('en-US')} of ${sheet.totalRows.toLocaleString('en-US')} rows. The full workbook is created when you accept.`;
}
function applyDraftCellStyle(sheet, td, row, col) {
    const style = sheet.styles[row]?.[col];
    td.style.backgroundColor = style?.fill ?? '';
    td.style.fontWeight = style?.bold ? '700' : '';
    // Read-only cells are dimmed by Handsontable; the workbook's own font colour
    // wins so a white-on-navy header stays readable.
    td.style.color = style?.color ?? '';
}
function DraftLoading() {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col gap-[var(--space-3)] p-[var(--space-4)]",
        "data-testid": Testids.assistant.documentDraftPreviewLoading,
        children: [
            /*#__PURE__*/ _jsx(SpecraSpinner, {
                label: "Loading draft preview"
            }),
            /*#__PURE__*/ _jsx(SpecraSurface, {
                className: "h-8 animate-pulse",
                elevation: "none"
            }),
            /*#__PURE__*/ _jsx(SpecraSurface, {
                className: "h-8 animate-pulse",
                elevation: "none"
            }),
            /*#__PURE__*/ _jsx(SpecraSurface, {
                className: "min-h-40 flex-1 animate-pulse",
                elevation: "none"
            })
        ]
    });
}
function DraftUnavailable() {
    return /*#__PURE__*/ _jsx(SpecraEmptyState, {
        "data-testid": Testids.assistant.documentDraftPreviewUnavailable,
        title: DRAFT_PREVIEW_UNAVAILABLE
    });
}
function WorkbookPreview({ preview }) {
    const [sheetName, setSheetName] = useState(preview.sheets[0]?.name ?? '');
    const active = preview.sheets.find((sheet)=>sheet.name === sheetName) ?? preview.sheets[0];
    if (!active) return /*#__PURE__*/ _jsx(DraftUnavailable, {});
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex min-h-0 flex-1 flex-col",
        children: [
            /*#__PURE__*/ _jsx(SpecraTabBar, {
                className: "px-[var(--space-4)]",
                "data-testid": Testids.assistant.documentDraftPreviewTabs,
                idBase: "document-draft-preview",
                label: "Workbook sheets",
                onValueChange: setSheetName,
                options: preview.sheets.map((sheet)=>({
                        label: sheet.name,
                        testId: Testids.assistant.documentDraftPreviewSheet,
                        value: sheet.name
                    })),
                value: active.name
            }),
            preview.sheets.map((sheet)=>/*#__PURE__*/ _jsxs(SpecraTabPanel, {
                    active: sheet.name === active.name,
                    className: "flex min-h-0 flex-1 flex-col",
                    idBase: "document-draft-preview",
                    value: sheet.name,
                    children: [
                        sheet.truncated ? /*#__PURE__*/ _jsx(SpecraText, {
                            className: "px-[var(--space-4)] py-[var(--space-2)]",
                            "data-testid": Testids.assistant.documentDraftPreviewLimit,
                            size: "caption",
                            tone: "muted",
                            children: sheetLimitLine(sheet)
                        }) : null,
                        /*#__PURE__*/ _jsx("div", {
                            className: "relative min-h-0 flex-1",
                            "data-testid": Testids.assistant.documentDraftPreviewGrid,
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "absolute inset-0 [&>div]:h-full [&_.ht-grid]:h-full [&_.ht-root-wrapper]:h-full",
                                children: /*#__PURE__*/ _jsx(HotTable, {
                                    afterRenderer: (td, row, col)=>applyDraftCellStyle(sheet, td, row, col),
                                    colHeaders: true,
                                    colWidths: sheet.colWidths.length > 0 ? sheet.colWidths : undefined,
                                    data: sheet.rows,
                                    disableVisualSelection: true,
                                    fixedRowsTop: sheet.frozenRows,
                                    height: "100%",
                                    licenseKey: "non-commercial-and-evaluation",
                                    readOnly: true,
                                    rowHeaders: true,
                                    width: "100%"
                                })
                            })
                        })
                    ]
                }, sheet.name))
        ]
    });
}
function DocumentPreview({ preview }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-h-0 flex-1 overflow-auto px-[var(--space-6)] py-[var(--space-4)]",
        children: [
            preview.truncated ? /*#__PURE__*/ _jsx(SpecraText, {
                className: "mb-[var(--space-4)]",
                "data-testid": Testids.assistant.documentDraftPreviewLimit,
                size: "caption",
                tone: "muted",
                children: "Showing the first pages. The full document is created when you accept."
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-[var(--space-3)]",
                children: preview.blocks.map((block, index)=>block.type === 'heading' ? /*#__PURE__*/ _jsx(SpecraHeading, {
                        size: "sm",
                        children: block.text
                    }, index) : /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        children: block.text
                    }, index))
            })
        ]
    });
}
function DeckPreview({ preview }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-h-0 flex-1 overflow-auto px-[var(--space-6)] py-[var(--space-4)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                className: "mb-[var(--space-4)]",
                size: "caption",
                tone: "muted",
                children: "The full deck is created when you accept."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-[var(--space-5)]",
                children: preview.slides.map((slide, index)=>/*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                size: "sm",
                                children: slide.title
                            }),
                            slide.bullets.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                                className: "mt-[var(--space-2)] list-disc space-y-[var(--space-2)] pl-[var(--space-5)]",
                                children: slide.bullets.map((bullet)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraText, {
                                            size: "body-ui",
                                            children: bullet
                                        })
                                    }, bullet))
                            }) : null
                        ]
                    }, `${slide.title}-${index}`))
            })
        ]
    });
}
export function DocumentDraftPreview({ documentType, source }) {
    const query = useQuery({
        queryFn: async ()=>{
            const bytes = await loadDraftBytes(source);
            return parseDraftOffice(bytes, documentType, {
                firstSheetOnly: previewFirstSheetOnly(source, bytes)
            });
        },
        queryKey: [
            'document-draft-preview',
            documentType,
            source.draftArtifactId ?? '',
            source.contents ?? '',
            source.sizeBytes ?? 0
        ],
        retry: false,
        staleTime: 5 * 60 * 1000
    });
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Draft document preview",
        className: "flex h-full min-h-0 flex-col",
        "data-testid": Testids.assistant.documentDraftPreview,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                className: "border-b border-border px-[var(--space-4)] py-[var(--space-2)]",
                size: "caption",
                tone: "muted",
                children: "Draft — not created yet"
            }),
            query.isPending ? /*#__PURE__*/ _jsx(DraftLoading, {}) : null,
            query.isError || query.data === undefined ? query.isPending ? null : /*#__PURE__*/ _jsx(DraftUnavailable, {}) : query.data.kind === 'xlsx' ? /*#__PURE__*/ _jsx(WorkbookPreview, {
                preview: query.data
            }) : query.data.kind === 'docx' ? /*#__PURE__*/ _jsx(DocumentPreview, {
                preview: query.data
            }) : /*#__PURE__*/ _jsx(DeckPreview, {
                preview: query.data
            })
        ]
    });
}
